import {
  Badge,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Grid,
  Stack,
  Text,
} from '@careflux/primitives';
import { DiffView } from './DiffView';
import { QueryState } from './QueryState';
import type { ComparisonDetailDialogProps, DetailBodyProps } from './components.types';
import { MAGNITUDE_LABELS, formatNumber } from './formatting';
import { MAGNITUDE_BADGE_VARIANT, summaryTextClasses } from './summaryQuality.styles';
import { useComparisonDetail } from './useSummaryQualityQueries';

const DetailBody = ({ detail }: DetailBodyProps) => (
  <Stack gap="md">
    <Grid columns={4} gap="md" className="rounded-lg bg-muted/50 p-4 text-center">
      <Stack gap="xs">
        <Text weight="bold" size="lg">{`${detail.metrics.similarityPercentage}%`}</Text>
        <Text size="xs" color="muted">
          Líkindi
        </Text>
      </Stack>
      <Stack gap="xs">
        <Text weight="bold" size="lg">
          {formatNumber(detail.metrics.wordDiffCount)}
        </Text>
        <Text size="xs" color="muted">
          Orðabreytingar
        </Text>
      </Stack>
      <Stack gap="xs">
        <Text weight="bold" size="lg">
          {formatNumber(detail.metrics.editDistance)}
        </Text>
        <Text size="xs" color="muted">
          Breytingafjarlægð
        </Text>
      </Stack>
      <Stack gap="xs" align="center">
        <Badge variant={MAGNITUDE_BADGE_VARIANT[detail.metrics.changeMagnitude]}>
          {MAGNITUDE_LABELS[detail.metrics.changeMagnitude]}
        </Badge>
        <Text size="xs" color="muted">
          Umfang
        </Text>
      </Stack>
    </Grid>

    {(detail.termsAdded.length > 0 || detail.termsRemoved.length > 0) && (
      <Stack gap="sm">
        <Text size="sm" weight="medium">
          Læknisfræðileg hugtök
        </Text>
        <div className="flex flex-wrap gap-2">
          {detail.termsAdded.map((term) => (
            <Badge key={`added-${term}`} variant="success" appearance="outline">{`+ ${term}`}</Badge>
          ))}
          {detail.termsRemoved.map((term) => (
            <Badge key={`removed-${term}`} variant="error" appearance="outline">{`− ${term}`}</Badge>
          ))}
        </div>
      </Stack>
    )}

    <Grid columns={{ base: 1, md: 2 }} gap="md">
      <Stack gap="xs">
        <Text size="sm" weight="medium">
          AI samantekt
        </Text>
        <pre className={summaryTextClasses}>{detail.modelText}</pre>
        <Text size="xs" color="muted">
          {`${formatNumber(detail.modelWordCount)} orð · ${formatNumber(detail.modelCharacterCount)} stafir`}
        </Text>
      </Stack>
      <Stack gap="xs">
        <Text size="sm" weight="medium">
          Vistuð samantekt
        </Text>
        <pre className={summaryTextClasses}>{detail.userText}</pre>
        <Text size="xs" color="muted">
          {`${formatNumber(detail.userWordCount)} orð · ${formatNumber(detail.userCharacterCount)} stafir`}
        </Text>
      </Stack>
    </Grid>

    <Stack gap="xs">
      <Text size="sm" weight="medium">
        Mismunur
      </Text>
      <DiffView html={detail.inlineDiffHtml} />
    </Stack>
  </Stack>
);

export const ComparisonDetailDialog = ({ audioId, onClose }: ComparisonDetailDialogProps) => {
  const detailQuery = useComparisonDetail(audioId);

  return (
    <Dialog open={audioId != null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent scrollable className="sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>Samanburður samantekta</DialogTitle>
          <DialogDescription>AI útgáfa á móti vistaðri útgáfu</DialogDescription>
        </DialogHeader>
        <QueryState
          isLoading={detailQuery.isLoading}
          isError={detailQuery.isError}
          error={detailQuery.error}
          onRetry={() => void detailQuery.refetch()}
          loadingLabel="Sæki samanburð"
          errorTitle="Samanburður hlóðst ekki"
        />
        {detailQuery.data && <DetailBody detail={detailQuery.data} />}
      </DialogContent>
    </Dialog>
  );
};
